<script>
  import {
    COLOR_TAB_ORDER,
    COLOR_TAB_STYLES,
    COLOR_TAB_DISPLAY_NAMES,
    MD_BREAKPOINT,
  } from '../../lib/logic/config.js';
  import { isPaperOnScreen } from '../../lib/logic/image-load-order.js';
  import PaperPage from './PaperPage.svelte';

  let {
    currentLocation = 0,
    numPapers = 0,
    allCardsData = [],
    currencySymbol = '$',
    unflippingIndex = -1,
    paperTabMap = {},
    activeColorKey = null,
    extraBufferedPages = [],
    zBoostedPages = [],
    pinnedBackOverrides = [],
    onColorTabJump = null,
    onSelect,
    onZoom,
    onTradeQuantity,
  } = $props();

  const isMobile = $derived(typeof window !== 'undefined' && window.innerWidth < MD_BREAKPOINT);

  // Smart buffer: compute which page indices to render
  const bufferedPages = $derived.by(() => {
    if (numPapers === 0) return [];

    const pages = new Set();
    const backBuffer = isMobile ? 1 : 3;
    const forwardBuffer = isMobile ? 2 : 3;

    if (isMobile) {
      for (
        let i = Math.max(0, currentLocation - backBuffer);
        i <= Math.min(numPapers - 1, currentLocation + forwardBuffer);
        i++
      ) {
        pages.add(i);
      }
    } else {
      // Always keep cover on desktop
      if (numPapers > 0) pages.add(0);

      if (currentLocation > 0) {
        pages.add(currentLocation - 1);
        for (let i = 1; i <= backBuffer; i++) {
          if (currentLocation - 1 - i >= 0) pages.add(currentLocation - 1 - i);
        }
      }
      if (currentLocation < numPapers) {
        pages.add(currentLocation);
        for (let i = 1; i <= forwardBuffer; i++) {
          if (currentLocation + i < numPapers) pages.add(currentLocation + i);
        }
      }
    }

    // Keep pinned pages in buffer during tab jumps
    for (const pi of extraBufferedPages) {
      if (pi >= 0 && pi < numPapers) pages.add(pi);
    }

    return Array.from(pages).sort((a, b) => a - b);
  });

  // Build all 7 tabs — active ones from paperTabMap, inactive ones dimmed at current location
  const allTabs = $derived.by(() => {
    const byKey = {};
    for (const [pi, info] of Object.entries(paperTabMap)) {
      byKey[info.key] = { paperIndex: Number(pi), ...info, enabled: true };
    }
    return COLOR_TAB_ORDER.map((key, i) => {
      if (byKey[key]) return byKey[key];
      return {
        key,
        label: COLOR_TAB_DISPLAY_NAMES[key] || key,
        tabIndex: i,
        totalTabs: COLOR_TAB_ORDER.length,
        paperIndex: currentLocation, // park at current location (unflipped, right side)
        enabled: false,
      };
    });
  });

  // Compute z-index for each page
  function getZIndex(paperIndex) {
    if (paperIndex < currentLocation) {
      return numPapers + paperIndex;
    }
    return numPapers - paperIndex;
  }
</script>

<div class="book-pages-container">
  {#if numPapers === 0}
    <p class="empty-message">No cards to display.</p>
  {:else}
    {#each bufferedPages as paperIndex (paperIndex)}
      <PaperPage
        {paperIndex}
        {isMobile}
        {currentLocation}
        isFlipped={paperIndex < currentLocation}
        isUnflippingBackward={paperIndex === unflippingIndex}
        zIndex={zBoostedPages.includes(paperIndex) ? 2500 : getZIndex(paperIndex)}
        overrideBackIndices={pinnedBackOverrides.find((o) => o.paperIndex === paperIndex) ?? null}
        isVisibleSpread={isPaperOnScreen({ paperIndex, currentLocation, isMobile })}
        {allCardsData}
        {currencySymbol}
        {onSelect}
        {onZoom}
        {onTradeQuantity}
      />
    {/each}

    <!-- Color divider tabs — only shown in color sort mode -->
    {#if Object.keys(paperTabMap).length > 0}
      {#each allTabs as tab (tab.key)}
        {@const style = COLOR_TAB_STYLES[tab.key]}
        {@const isFlipped = tab.paperIndex < currentLocation}
        {@const isActive = tab.key === activeColorKey}
        {@const totalTabs = COLOR_TAB_ORDER.length}
        {@const tabIdx = COLOR_TAB_ORDER.indexOf(tab.key)}
        <div class="tab-page" class:flipped={isFlipped} style:z-index={getZIndex(tab.paperIndex)}>
          <button
            class="divider-tab tab-front"
            class:active-tab={isActive}
            class:tab-disabled={!tab.enabled}
            disabled={!tab.enabled}
            style="
            background: {style?.bg || '#888'};
            color: {style?.text || '#fff'};
            top: calc(10% + {(tabIdx + 0.5) / totalTabs} * 80% - 14px);
          "
            onclick={() => onColorTabJump?.(tab.key)}
            title={tab.label}
          >
            {#if style?.svg}
              <img class="mana-icon" src={style.svg} alt={tab.label} />
            {:else}
              {style?.label || tab.key}
            {/if}
          </button>
          <button
            class="divider-tab tab-back"
            class:active-tab={isActive}
            class:tab-disabled={!tab.enabled}
            disabled={!tab.enabled}
            style="
            background: {style?.bg || '#888'};
            color: {style?.text || '#fff'};
            top: calc(10% + {(tabIdx + 0.5) / totalTabs} * 80% - 14px);
          "
            onclick={() => onColorTabJump?.(tab.key)}
            title={tab.label}
          >
            {#if style?.svg}
              <img class="mana-icon" src={style.svg} alt={tab.label} />
            {:else}
              {style?.label || tab.key}
            {/if}
          </button>
        </div>
      {/each}
    {/if}
  {/if}
</div>

<style>
  .book-pages-container {
    position: relative;
    transform-style: preserve-3d;
    z-index: var(--z-book-pages, 10);
    min-height: var(--min-book-pages-height, 300px);
    width: 100%;
    height: auto;
    display: grid;
    transform: translateX(0%);
  }

  .empty-message {
    text-align: center;
    color: #9ca3af;
    padding: 2.5rem;
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Hide divider tabs on mobile — replaced by color picker pill */
  @media (max-width: 767px) {
    .tab-page {
      display: none;
    }
  }

  /* Tab page — a lightweight 3D element that flips with the real page */
  .tab-page {
    width: 100%;
    height: 100%;
    position: relative;
    transform-origin: left center;
    transition: transform var(--dur-page-flip) var(--ease-page-flip);
    transform-style: preserve-3d;
    grid-area: 1 / 1 / -1 / -1;
    pointer-events: none;
  }

  .tab-page.flipped {
    transform: rotateY(-180deg);
  }

  /* Shared tab styling */
  .divider-tab {
    position: absolute;
    pointer-events: auto;
    border: none;
    font-size: 0.55rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    cursor: pointer;
    white-space: nowrap;
    line-height: 1;
    text-align: center;
    height: 28px;
    min-width: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    backface-visibility: hidden;
    box-shadow:
      1px 1px 3px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
    transition:
      transform 0.15s ease,
      box-shadow 0.15s;
  }

  .mana-icon {
    width: 20px;
    height: 20px;
    display: block;
    pointer-events: none;
  }

  /* Front tab — protrudes from right edge */
  .tab-front {
    right: 0;
    transform: translateX(85%);
    border-radius: 0 14px 14px 0;
    padding: 4px 5px 4px 3px;
  }

  .tab-front:hover,
  .tab-front.active-tab {
    transform: translateX(95%);
    box-shadow:
      2px 1px 5px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }

  /* Back tab — protrudes from left edge when page is flipped */
  .tab-back {
    right: 0;
    transform: rotateY(180deg) translateX(-85%);
    border-radius: 14px 0 0 14px;
    padding: 4px 3px 4px 5px;
  }

  .tab-back:hover,
  .tab-back.active-tab {
    transform: rotateY(180deg) translateX(-95%);
    box-shadow:
      2px 1px 5px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }

  .tab-disabled {
    opacity: 0.25;
    cursor: default;
  }

  @media (min-width: 768px) {
    .divider-tab {
      height: 34px;
      min-width: 34px;
      font-size: 0.6rem;
    }

    .tab-front {
      padding: 5px 6px 5px 4px;
      border-radius: 0 17px 17px 0;
    }

    .tab-back {
      padding: 5px 4px 5px 6px;
      border-radius: 17px 0 0 17px;
    }

    .mana-icon {
      width: 24px;
      height: 24px;
    }
  }

  /* Desktop: pages in right half of binder */
  @media (min-width: 768px) {
    .book-pages-container {
      width: 50%;
      transform: translateX(100%);
      height: auto;
      min-height: unset;
    }
  }
</style>
